import type {} from '@mui/x-data-grid/themeAugmentation';
import { alpha, createTheme } from '@mui/material/styles';
import {
  amber,
  black,
  blue,
  emerald,
  green,
  purple,
  red,
  slate,
  white,
} from '@/lib/colours';

const slateGrey = {
  50: slate[50],
  100: slate[100],
  200: slate[200],
  300: slate[300],
  400: slate[400],
  500: slate[500],
  600: slate[600],
  700: slate[700],
  800: slate[800],
  900: slate[900],
  A100: slate[100],
  A200: slate[200],
  A400: slate[400],
  A700: slate[700],
};

const lightPalette = {
  common: {
    black: black[900],
    white: white[50],
  },
  grey: slateGrey,
  background: {
    default: slate[50],
    paper: white[50],
  },
  text: {
    primary: slate[900],
    secondary: slate[600],
    disabled: slate[700],
  },
  action: {
    disabledBackground: slate[200],
    disabled: slate[700],
  },
  divider: alpha(black[900], 0.12),
  primary: {
    main: blue['600'],
    light: blue['200'],
    dark: blue['900'],
  },
  secondary: {
    main: emerald['600'],
    light: emerald['200'],
    dark: emerald['800'],
  },
  error: {
    main: red['600'],
    light: red['200'],
    dark: red['800'],
  },
  warning: {
    main: amber['600'],
    light: amber['200'],
    dark: amber['800'],
  },
  info: {
    main: blue['600'],
    light: blue['200'],
    dark: blue['800'],
  },
  success: {
    main: green['600'],
    light: green['200'],
    dark: green['800'],
  },
  ebvCategory: {
    indexes: purple[300],
  },
  publicSearch: {
    aiAccent: purple[500],
    aiAccentDark: purple[600],
  },
  DataGrid: {
    headerBg: white[50],
  },
};

const darkPalette = {
  mode: 'dark' as const,
  common: {
    black: black[900],
    white: white[50],
  },
  grey: slateGrey,
  background: {
    default: slate[900],
    paper: slate[800],
  },
  text: {
    primary: '#F1F5F9',
    secondary: slate[300],
    disabled: alpha('#F1F5F9', 0.38),
  },
  divider: alpha(white[50], 0.12),
  primary: {
    main: blue[400],
    light: blue[300],
    dark: blue[600],
  },
  secondary: {
    main: emerald[400],
    light: emerald[300],
    dark: emerald[600],
  },
  error: {
    main: red[400],
    light: red[300],
    dark: red[600],
  },
  warning: {
    main: amber[400],
    light: amber[300],
    dark: amber[600],
  },
  info: {
    main: blue[400],
    light: blue[300],
    dark: blue[600],
  },
  success: {
    main: green[400],
    light: green[300],
    dark: green[600],
  },
  ebvCategory: {
    indexes: purple[400],
  },
  publicSearch: {
    aiAccent: purple[300],
    aiAccentDark: purple[400],
  },
  DataGrid: {
    headerBg: slate[700],
  },
};

export const theme = createTheme({
  // `media` is the default when both schemes exist; it ignores `setMode`. Use `data`
  // so toggles work and match InitColorSchemeScript (`data-mui-color-scheme`).
  cssVariables: {
    colorSchemeSelector: 'data',
  },
  palette: lightPalette,
  colorSchemes: {
    dark: {
      palette: darkPalette,
    },
  },
  typography: {
    fontFamily: 'inherit',
    h1: {},
    h2: {},
    h3: {},
    h4: {},
    h5: {},
    h6: {},
    subtitle1: undefined,
    subtitle2: undefined,
    body1: {},
    body2: undefined,
    caption: undefined,
    displaylarge: {
      fontSize: '57px',
      fontWeight: 400,
      lineHeight: '64px',
      letterSpacing: '-0.25px',
    },
    displaymedium: {
      fontSize: '45px',
      fontWeight: 400,
      lineHeight: '52px',
    },
    displaysmall: {
      fontSize: '36px',
      fontWeight: 400,
      lineHeight: '44px',
    },
    headlinelarge: {
      fontSize: '32px',
      fontWeight: 400,
      lineHeight: '40px',
    },
    headlinemedium: {
      fontSize: '28px',
      fontWeight: 400,
      lineHeight: '36px',
    },
    headlinesmall: {
      fontSize: '24px',
      fontWeight: 400,
      lineHeight: '32px',
    },
    titlelarge: {
      fontSize: '22px',
      fontWeight: 400,
      lineHeight: '28px',
    },
    titlemedium: {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '24px',
      letterSpacing: '0.1px',
    },
    titlesmall: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      letterSpacing: '0.1px',
    },
    labellarge: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      letterSpacing: '0.1px',
    },
    labelmedium: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '16px',
      letterSpacing: '0.5px',
    },
    labelsmall: {
      fontSize: '11px',
      fontWeight: 500,
      lineHeight: '16px',
      letterSpacing: '0.5px',
    },
    bodylarge: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0.5px',
    },
    bodymedium: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0.25px',
    },
    bodysmall: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: '0.4px',
    },
    '14/400/24': {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '24px',
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          displaylarge: 'h1',
          displaymedium: 'h1',
          displaysmall: 'h1',
          headlinelarge: 'h2',
          headlinemedium: 'h2',
          headlinesmall: 'h2',
          titlelarge: 'h3',
          titlemedium: 'h3',
          titlesmall: 'h3',
          labellarge: 'p',
          labelmedium: 'p',
          labelsmall: 'p',
          bodylarge: 'p',
          bodymedium: 'p',
          bodysmall: 'p',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: ({ theme }) => ({
          color: theme.palette.error.main,
          '.Mui-disabled &': {
            color: theme.palette.text.disabled,
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        asterisk: ({ theme }) => ({
          color: theme.palette.error.main,
          '.Mui-disabled &': {
            color: theme.palette.text.disabled,
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&[type=number]': {
            MozAppearance: 'textfield',
          },
          '&[type=number]::-webkit-outer-spin-button, &[type=number]::-webkit-inner-spin-button':
            {
              WebkitAppearance: 'none',
              margin: 0,
            },
        },
        // Match compact date-picker adornments: smaller icons in text fields, autocomplete, etc.
        root: {
          '& .MuiInputAdornment-positionEnd': {
            paddingRight: '8px',
          },
          '& .MuiInputAdornment-root .MuiIconButton-root': {
            padding: '6px',
          },
          '& .MuiInputAdornment-root .MuiSvgIcon-root': {
            fontSize: '1.25rem',
          },
          '&.MuiInputBase-sizeSmall': {
            '& .MuiInputAdornment-positionEnd': {
              paddingRight: '6px',
            },
            '& .MuiInputAdornment-root .MuiIconButton-root': {
              padding: '4px',
            },
            '& .MuiInputAdornment-root .MuiSvgIcon-root': {
              fontSize: '1.125rem',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: ({ ownerState }) => ({
          fontSize: ownerState.size === 'small' ? '1.125rem' : '1.25rem',
          right: ownerState.size === 'small' ? 6 : 8,
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popupIndicator: ({ ownerState }) => ({
          padding: ownerState.size === 'small' ? '4px' : '6px',
          '& .MuiSvgIcon-root': {
            fontSize: ownerState.size === 'small' ? '1.125rem' : '1.25rem',
          },
        }),
        clearIndicator: ({ ownerState }) => ({
          padding: ownerState.size === 'small' ? '4px' : '6px',
          '& .MuiSvgIcon-root': {
            fontSize: ownerState.size === 'small' ? '1.125rem' : '1.25rem',
          },
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: 'none',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          textTransform: 'none',
          letterSpacing: '0.1px',
          lineHeight: '20px',
          boxShadow: 'none',
        },
        text: ({ theme }) => ({
          padding: '6px 16px',
          '&:hover': {
            backgroundColor: blue[100],
            ...theme.applyStyles('dark', {
              backgroundColor: alpha(blue[400], 0.16),
            }),
          },
        }),
        sizeMedium: {
          paddingTop: '10px',
          paddingBottom: '10px',
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: '999px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
      variants: [
        {
          props: { variant: 'active' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.success.light,
            color: theme.palette.success.dark,
            ...theme.applyStyles('dark', {
              backgroundColor: alpha(theme.palette.success.main, 0.28),
              color: theme.palette.success.light,
            }),
          }),
        },
      ],
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          '&.Mui-selected': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiDataGrid: {},
    MuiFab: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          marginTop: '12px',
          marginBottom: '12px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '24px 40px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: '24px 40px',
          paddingTop: '24px !important',
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px',
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            borderRadius: '12px',
          },
        },
      },
    },
  },
});
